import type { ReflectionEntry, ReflectionNote } from "@couples/shared";
import { CheckCircle2, Clock, Trash2 } from "lucide-react";

import { withAlpha } from "../lib/color";
import { formatDateJa } from "../lib/dates";

interface Props {
  note: ReflectionNote;
  /** このノートに紐づく振り返り(自分・相手それぞれ書いていれば入る) */
  selfEntry: ReflectionEntry | undefined;
  partnerEntry: ReflectionEntry | undefined;
  partnerName: string;
  selfName: string;
  selfColor: string;
  partnerColor: string;
  onDeleteEntry: (entry: ReflectionEntry) => void;
  /** 自分がまだ書いていないとき、追記シートを開く */
  onWriteOwn: () => void;
  onResolve: () => void;
}

/** 状態チップの見た目。色はモックと同じ(記入待ち=sub / 話し合い中=warn / 解決済み=accent) */
function StatusChip({ note, bothWritten }: { note: ReflectionNote; bothWritten: boolean }) {
  if (note.resolved) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-teal)] px-2 py-0.5 text-[10.5px] font-bold text-white">
        <CheckCircle2 className="size-2.5" aria-hidden />
        解決済み
      </span>
    );
  }
  if (bothWritten) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--color-warn)_14%,transparent)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--color-warn)]">
        <Clock className="size-2.5" aria-hidden />
        話し合い中
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-line)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--color-sub)]">
      <Clock className="size-2.5" aria-hidden />
      記入待ち
    </span>
  );
}

function EntryBlock({
  entry,
  name,
  color,
  isSelf,
  onDelete,
}: {
  entry: ReflectionEntry;
  name: string;
  color: string;
  isSelf: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-lg bg-[var(--color-bg)] p-3 pt-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span
          className="inline-block rounded-sm px-2 py-0.5 text-[11px] font-semibold"
          style={{ backgroundColor: withAlpha(color, "1a"), color }}
        >
          {name}
        </span>
        {isSelf && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="自分の振り返りを削除"
            className="text-[var(--color-sub)]"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
      <p className="mb-0.5 text-[10px] text-[var(--color-sub)]">原因について思うこと</p>
      <p className="mb-1.5 text-[12.5px] leading-relaxed whitespace-pre-line">{entry.cause}</p>
      <p className="mb-0.5 text-[10px] text-[var(--color-sub)]">次に気をつけたいこと</p>
      <p className="text-[12.5px] leading-relaxed whitespace-pre-line">{entry.nextStep}</p>
    </div>
  );
}

export default function ReflectionCard({
  note,
  selfEntry,
  partnerEntry,
  partnerName,
  selfName,
  selfColor,
  partnerColor,
  onDeleteEntry,
  onWriteOwn,
  onResolve,
}: Props) {
  const bothWritten = selfEntry !== undefined && partnerEntry !== undefined;
  const canResolve = bothWritten && !note.resolved;

  return (
    <div className="rounded-xl bg-white p-4 pt-3.5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="diary-stamp">{formatDateJa(note.occurredOn)}</span>
        <StatusChip note={note} bothWritten={bothWritten} />
      </div>

      <h3 className="mb-3 text-sm leading-relaxed font-bold">{note.title}</h3>

      <div className="flex flex-col gap-2.5">
        {selfEntry !== undefined ? (
          <EntryBlock
            entry={selfEntry}
            name={selfName}
            color={selfColor}
            isSelf
            onDelete={() => {
              onDeleteEntry(selfEntry);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={onWriteOwn}
            className="rounded-lg border border-dashed border-[var(--color-line)] p-3 text-left text-xs font-semibold text-[var(--color-accent)]"
          >
            自分の振り返りを書く
          </button>
        )}

        {partnerEntry !== undefined ? (
          <EntryBlock
            entry={partnerEntry}
            name={partnerName}
            color={partnerColor}
            isSelf={false}
            onDelete={() => {
              /* 相手の振り返りは消せない(削除ボタンを出さないため呼ばれない) */
            }}
          />
        ) : (
          <p className="rounded-lg bg-[var(--color-bg)] p-3 text-xs text-[var(--color-sub)]">
            {partnerName}さんの振り返りはまだです。
          </p>
        )}
      </div>

      {canResolve && (
        <div className="mt-3 border-t border-[var(--color-line)] pt-2.5 text-center">
          <button
            type="button"
            onClick={onResolve}
            className="text-xs font-bold text-[var(--color-accent)]"
          >
            仲直りしたら「解決済み」にする
          </button>
        </div>
      )}
    </div>
  );
}
